import React from "react";
import { Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { ID, Outfit, OutfitStatus } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

export function OutfitsScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [outfits, setOutfits] = React.useState<Outfit[] | null>(null);
  const [outfitsLoading, setOutfitsLoading] = React.useState(false);
  const [outfitsError, setOutfitsError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [detail, setDetail] = React.useState<Outfit | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setOutfitsLoading(true);
    setOutfitsError(null);
    try {
      setOutfits(await service.outfits.list());
    } catch (err) {
      setOutfitsError(errorMessage(err));
    } finally {
      setOutfitsLoading(false);
    }
  }, [service]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading outfits" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const setStatus = async (outfit: Outfit, status: OutfitStatus) => {
    setActionError(null);
    try { await service.outfits.updateOutfit(outfit.id, { status }); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };
  const remove = async (outfit: Outfit) => {
    setActionError(null);
    try { await service.outfits.deleteOutfit(outfit.id); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };

  return (
    <div>
      <PageHeader eyebrow="More" title="Outfits" actions={<Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add outfit</Button>} />
      <div className="pob-pad">
        {outfitsLoading && !outfits && <LoadingState label="Loading outfits" />}
        {outfitsError && <ErrorState message={outfitsError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}
        {outfits && (
          <Card padding="0">
            <DataTable
              rows={outfits}
              emptyLabel="No outfits yet"
              onRowClick={(row) => setDetail(row)}
              columns={[
                { key: "personName", header: "Person" },
                { key: "item", header: "Item" },
                { key: "vendorName", header: "Designer/vendor", muted: true },
                { key: "cost", header: "Cost", width: 100, render: (row) => row.cost != null ? `PKR ${row.cost.toLocaleString()}` : "—" },
                { key: "status", header: "Status", width: 130, render: (row) => <Select size="sm" value={row.status} onChange={(e) => setStatus(row, e.target.value as OutfitStatus)} options={[{ value: "planning", label: "Planning" }, { value: "ordered", label: "Ordered" }, { value: "in_fitting", label: "In fitting" }, { value: "ready", label: "Ready" }]} /> },
                { key: "fittings", header: "Fittings", width: 80, render: (row) => row.fittings.length },
                { key: "actions", header: "", width: 90, render: (row) => <Button size="sm" variant="danger" onClick={() => remove(row)}>Remove</Button> }
              ]}
            />
          </Card>
        )}
      </div>
      {adding && <AddOutfitDialog onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }} />}
      {detail && <OutfitDetailDialog outfit={detail} onClose={() => setDetail(null)} onChanged={async (updated) => setDetail(updated)} onReload={load} />}
    </div>
  );
}

function AddOutfitDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [personId, setPersonId] = React.useState(workspace!.people[0]?.id || "");
  const [eventId, setEventId] = React.useState(workspace!.events[0]?.id || "");
  const [item, setItem] = React.useState("");
  const [vendorId, setVendorId] = React.useState("");
  const [cost, setCost] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!personId || !eventId || !item.trim()) { setFormError("Choose a person, celebration and item."); return; }
    try {
      await service.outfits.createOutfit({ personId, eventId, item: item.trim(), vendorId: vendorId || undefined, cost: cost ? Number(cost) : undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title="Add outfit" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Person"><Select value={personId} onChange={(e) => setPersonId(e.target.value)} options={workspace!.people.map((p) => ({ value: p.id, label: p.name }))} /></Field>
        <Field label="Celebration"><Select value={eventId} onChange={(e) => setEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        <Field label="Item"><Input value={item} onChange={(e) => setItem(e.target.value)} placeholder="Sherwani" /></Field>
        <Field label="Designer/vendor (optional)"><Select value={vendorId} placeholder="None" onChange={(e) => setVendorId(e.target.value)} options={workspace!.vendors.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Cost (optional)"><Input value={cost} type="number" onChange={(e) => setCost(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}

function OutfitDetailDialog({ outfit, onClose, onChanged, onReload }: { outfit: Outfit; onClose: () => void; onChanged: (o: Outfit) => void; onReload: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [date, setDate] = React.useState("");
  const [location, setLocation] = React.useState("");
  const { submitting, guardedSubmit } = useSubmitGuard();

  const addFitting = () => guardedSubmit(async () => {
    const updated = await service.outfits.addFitting(outfit.id, { date: date || undefined, location: location || undefined });
    onChanged(updated);
    setDate("");
    setLocation("");
    await onReload();
  });

  const removeFitting = async (fittingId: ID) => {
    const updated = await service.outfits.deleteFitting(outfit.id, fittingId);
    onChanged(updated);
    await onReload();
  };

  return (
    <Dialog title={`${outfit.personName} — ${outfit.item}`} onClose={onClose} footer={<Button size="sm" onClick={onClose}>Close</Button>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {outfit.notes && <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{outfit.notes}</p>}
        <div>
          <span style={{ font: "var(--type-label)", display: "block", marginBottom: "var(--space-2)" }}>Fittings</span>
          {outfit.fittings.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>None scheduled yet.</span>}
          {outfit.fittings.map((f) => (
            <div key={f.id} style={{ display: "flex", justifyContent: "space-between", padding: "var(--space-2) 0", borderBottom: "1px solid var(--divider)" }}>
              <span style={{ font: "var(--type-body-sm)" }}>{f.date || "No date"} {f.location ? `· ${f.location}` : ""}</span>
              <button type="button" onClick={() => removeFitting(f.id)} style={{ border: 0, background: "transparent", color: "var(--text-tertiary)" }}>Remove</button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-end" }}>
          <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Location"><Input value={location} onChange={(e) => setLocation(e.target.value)} /></Field>
          <Button size="sm" onClick={addFitting} disabled={submitting}>{submitting ? "Adding…" : "Add fitting"}</Button>
        </div>
      </div>
    </Dialog>
  );
}
